/**
 * What a recursive ACL change applies and what it can widen — the words every consent surface shares (S8 review fixes
 * D, opus F2 / F3 / F10): the Propagate dialog and its typed confirmation, the Transfers Restart confirmation and the
 * MCP approval. The entry lines name every entry the change carries (object ids always shown, `describeAclPrincipal`);
 * the warnings are the default-scope effect (NOTES rows R6 / A9), the mask Azure recalculates for a remove (R5 vi) or
 * for a merge that does not set a scope's mask (C7), and the mask a merge writes onto every path (F3, R5 ii).
 */
import type { StorageAclChange, StorageAclEntry } from '../../types/azureStorage';
import { formatPermissions } from './azureStorageAcl';
import { describeAclEntry, describeAclPrincipal } from './azureStorageAclEffective';
import { aclModifyMaskLine } from './azureStorageAclJobText';
import { hnsServiceFacts, type HnsServiceFacts } from './azureStorageHnsFacts';

export type AclChangeTextFacts = Pick<HnsServiceFacts, 'accessOnlySetKeepsDefaults' | 'defaultEntriesOnFiles' | 'recursiveRemoveRecalculatesMask' | 'recursiveModifyKeepsExplicitMask'>;

/** "Entry: user 1b2c3d4e…: read, execute" per entry of a set / merge; "Remove: user 1b2c3d4e… (default)" per removed entry. */
export function aclChangeEntryLines(change: StorageAclChange, displayNames: Record<string, string> = {}): string[] {
  if (change.mode === 'remove') return change.entries.map((entry) => `Remove: ${describeAclPrincipal(entry, displayNames)}${entry.scope === 'default' ? ' (default)' : ''}`);
  return change.entries.map((entry) => `Entry: ${describeAclEntry(entry, displayNames)}`);
}

/** What happens to default entries (rows R6 / A9: `accessOnlySetKeepsDefaults`, `defaultEntriesOnFiles`). */
export function defaultScopeSentence(change: StorageAclChange, root: string, facts: Pick<HnsServiceFacts, 'accessOnlySetKeepsDefaults' | 'defaultEntriesOnFiles'>): string | undefined {
  const hasDefault = change.entries.some((e) => e.scope === 'default');
  if (change.mode === 'set' && !hasDefault) {
    return facts.accessOnlySetKeepsDefaults.recursive ? `Default entries of directories under ${root} are kept.` : `Default entries of directories under ${root} are removed.`;
  }
  if (!hasDefault) return undefined;
  return facts.defaultEntriesOnFiles === 'ignored'
    ? `Default entries apply to directories only; files under ${root} ignore them.`
    : `Default entries apply to directories only; every file under ${root} will be listed as a failure.`;
}

/** Row R5 (vi): a recursive remove cannot carry a mask, and Azure recalculates the mask of every scope it removes from. */
export function removeMaskSentence(change: StorageAclChange, facts: Pick<HnsServiceFacts, 'recursiveRemoveRecalculatesMask'>): string | undefined {
  if (change.mode !== 'remove' || !facts.recursiveRemoveRecalculatesMask) return undefined;
  return 'Azure recalculates the mask of every path it removes an entry from, so the owning group and the remaining named entries can gain permissions the old mask blocked.';
}

/**
 * Review opus F3 (row R5 ii — Azure keeps an explicit mask): a merge that carries a scope's mask writes it onto EVERY
 * path, replacing each path's own mask, so a path whose mask was narrower opens up to its named entries and owning group.
 */
export function mergeMaskSentences(change: StorageAclChange, root: string, facts: Pick<HnsServiceFacts, 'recursiveModifyKeepsExplicitMask'>): string[] {
  if (change.mode !== 'modify' || !facts.recursiveModifyKeepsExplicitMask) return [];
  return change.entries.filter((entry: StorageAclEntry) => entry.type === 'mask').map((mask) => mergeMaskSentence(mask, root));
}

export function mergeMaskSentence(mask: Pick<StorageAclEntry, 'scope' | 'permissions'>, root: string): string {
  const bits = formatPermissions(mask.permissions);
  return mask.scope === 'access'
    ? `This merge writes the access mask ${bits} onto ${root} and every path under it, replacing each path's own mask: on a path whose mask was narrower, its named entries and owning group gain the permissions that mask blocked.`
    : `This merge writes the default mask ${bits} onto ${root} and every directory under it, replacing each directory's own default mask: where that mask was narrower, files and directories created there later give their named entries and owning group more than it allowed.`;
}

/** Every warning of a recursive change, in the order the surfaces show them. */
export function aclChangeWarnings(change: StorageAclChange, root: string, facts: AclChangeTextFacts = hnsServiceFacts()): string[] {
  return [defaultScopeSentence(change, root, facts), removeMaskSentence(change, facts), aclModifyMaskLine(change, facts), ...mergeMaskSentences(change, root, facts)]
    .filter((line): line is string => line !== undefined);
}
